import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  Grid,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { colorUtils } from '../theme';
import { getSeverityChipSx, getStatusChipSx, getStatusColor, getPriorityColor } from '../theme/mappers/ticketMappers';

// ─── Demo Data (fully congruous for presentation) ───────────────────────────
//
// Story: busy MSSP SOC, 24-hour snapshot
//   • 1,247 raw alerts ingested → 526 events/min across integrations
//   • 154 open tickets total (7 critical + 18 high + 43 medium + 86 low)
//   • Status breakdown open sum = 18+24+68+44 = 154  ✓
//   • 4 SLA breaches (2.6% of open — realistic for a busy team)
//   • Socmate MTTR = 12 min vs 30 min SOAR vs 240 min manual

const demoStatusBreakdown = [
  { status: 'new',        label: 'New',        count: 18 },
  { status: 'assigned',   label: 'Assigned',   count: 24 },
  { status: 'in_progress',label: 'In Progress',count: 68 },
  { status: 'pending',    label: 'Pending',    count: 44 },
  { status: 'resolved',   label: 'Resolved',   count: 47 },
  { status: 'closed',     label: 'Closed',     count: 231 },
];

const demoMyTickets = [
  {
    id: 'TKT-1842',
    title: 'Ransomware precursor activity detected on PROD-DB-01',
    priority: 'critical',
    status: 'in_progress',
    lastAction: 'Host isolated, forensic image collection in progress',
  },
  {
    id: 'TKT-1838',
    title: 'C2 beacon on endpoint HR-PC-204 — Cobalt Strike signature',
    priority: 'high',
    status: 'in_progress',
    lastAction: 'YARA scan complete, awaiting threat intel enrichment',
  },
  {
    id: 'TKT-1821',
    title: 'Login anomaly for svc_backup — 12 countries in 4 hours',
    priority: 'medium',
    status: 'pending',
    lastAction: 'Escalated to IT Ops for immediate password rotation',
  },
];

const demoUnassignedTickets = [
  { id: 'TKT-1847', title: 'Lateral movement detected from WS-FIN-088 to DC-PROD-02', priority: 'critical', created: '8 min ago' },
  { id: 'TKT-1845', title: 'Phishing campaign targeting Finance & Legal — 34 recipients', priority: 'critical', created: '31 min ago' },
  { id: 'TKT-1841', title: 'Brute force on VPN gateway — 340 attempts in 6 minutes', priority: 'high', created: '52 min ago' },
  { id: 'TKT-1836', title: 'Malicious email attachment — LockBit 3.0 variant confirmed', priority: 'high', created: '1h 14m ago' },
];

const demoTopCategories = [
  { category: 'Phishing',            count: 47 },
  { category: 'Malware',             count: 32 },
  { category: 'Unauthorized Access', count: 24 },
  { category: 'Suspicious Activity', count: 19 },
  { category: 'Policy Violation',    count: 11 },
];

const demoTopClients = [
  { name: 'Acme Financial Corp',   alerts: 38 },
  { name: 'TechVault Solutions',   alerts: 27 },
  { name: 'Meridian Healthcare',   alerts: 21 },
  { name: 'Global Logistics Ltd',  alerts: 14 },
];

// 12 bars covering last 24h — gradual build toward peak, recent spike
const demoTrendData = [52, 64, 48, 71, 89, 103, 125, 118, 142, 156, 168, 111];

const demoMttr = { socmateMin: 12, manualMin: 240, soarMin: 30 };

// ─── Component ───────────────────────────────────────────────────────────────

const OverviewPageDemo: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('24h');

  const totalIngestion = 526;
  const rawAlerts = 1247;
  const slaBreachCount = 4;
  const totalStatusCount = demoStatusBreakdown.reduce((s, x) => s + x.count, 0);
  const maxCategoryCount = Math.max(...demoTopCategories.map((c) => c.count));
  const trendMax = Math.max(...demoTrendData);

  const cardSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1.5,
    p: 2.5,
    height: '100%',
  };

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
      variant="caption"
      sx={{
        color: theme.palette.text.tertiary,
        fontWeight: 700,
        display: 'block',
        mb: 1.5,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Overview
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Security operations at a glance
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small">
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                sx={{
                  height: 40,
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.background.tertiary,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  minWidth: 120,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { backgroundColor: theme.palette.background.secondary },
                }}
              >
                <MenuItem value="24h" sx={{ fontSize: '0.8rem' }}>Last 24 hours</MenuItem>
                <MenuItem value="7d"  sx={{ fontSize: '0.8rem' }}>Last 7 days</MenuItem>
                <MenuItem value="30d" sx={{ fontSize: '0.8rem' }}>Last 30 days</MenuItem>
              </Select>
            </FormControl>
            <IconButton sx={{
              bgcolor: theme.palette.background.tertiary,
              color: theme.palette.info.main,
              borderRadius: 1,
              width: 40,
              height: 40,
              transition: 'all 0.4s ease',
              '&:hover': { bgcolor: theme.palette.info.main, color: theme.palette.background.tertiary, transform: 'rotate(180deg)' },
            }}>
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Severity KPI Cards ─────────────────────────────────────────────── */}
        {/* Total open: 7+18+43+86 = 154, matches open status sum (18+24+68+44 = 154) */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Critical', count: 7,  color: theme.palette.severity.critical },
            { label: 'High',     count: 18, color: theme.palette.severity.high },
            { label: 'Medium',   count: 43, color: theme.palette.severity.medium },
            { label: 'Low',      count: 86, color: theme.palette.severity.low },
          ].map((kpi) => (
            <Grid key={kpi.label} size={{ xs: 6, sm: 3 }}>
              <Box sx={{
                ...cardSx,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateY(-2px)' },
              }}>
                <Typography sx={{
                  color: theme.palette.text.tertiary,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>
                  {kpi.label}
                </Typography>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: kpi.color, lineHeight: 1.1 }}>
                  {kpi.count}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* ── Three-Column Layout ────────────────────────────────────────────── */}
        <Grid container spacing={2.5}>

          {/* ═══ LEFT COLUMN ═════════════════════════════════════════════════ */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* SLA Breaches */}
              <Box sx={cardSx}>
                <SectionLabel>SLA Breaches</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: slaBreachCount > 0 ? theme.palette.error.main : theme.palette.success.main,
                    lineHeight: 1.1,
                  }}>
                    {slaBreachCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    tickets past due
                  </Typography>
                </Box>
              </Box>

              {/* Data Ingestion */}
              <Box sx={cardSx}>
                <SectionLabel>Data Ingestion</SectionLabel>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.1 }}>
                  {totalIngestion.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  events / min
                </Typography>
              </Box>

              {/* Alert Volume */}
              <Box sx={cardSx}>
                <SectionLabel>Alert Volume (24h)</SectionLabel>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.1 }}>
                  {rawAlerts.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  raw alerts ingested
                </Typography>
              </Box>

              {/* Ticket Status */}
              <Box sx={cardSx}>
                <SectionLabel>Ticket Status</SectionLabel>
                <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', height: 12, mb: 2 }}>
                  {demoStatusBreakdown.map((s) => (
                    <Box key={s.status} sx={{
                      width: `${(s.count / totalStatusCount) * 100}%`,
                      backgroundColor: getStatusColor(s.status, theme),
                      transition: 'width 0.3s ease',
                    }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {demoStatusBreakdown.map((s) => (
                    <Box key={s.status} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(s.status, theme),
                          flexShrink: 0,
                        }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {s.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}>
                        {s.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* ═══ CENTER COLUMN ═══════════════════════════════════════════════ */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* My Open Tickets */}
              <Box sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionLabel>My Open Tickets</SectionLabel>
                  <Chip
                    label={`${demoMyTickets.length}`}
                    size="small"
                    sx={{
                      backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: '0.70rem',
                      borderRadius: 1,
                      height: 22,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {demoMyTickets.map((ticket) => (
                    <Box key={ticket.id} sx={{
                      p: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateX(4px)' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0,
                        }}>
                          {ticket.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                          <Chip
                            label={ticket.status.replace('_', ' ')}
                            size="small"
                            sx={getStatusChipSx(getStatusColor(ticket.status, theme))}
                          />
                          <Chip
                            label={ticket.priority}
                            size="small"
                            sx={getSeverityChipSx(getPriorityColor(ticket.priority, theme))}
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        fontStyle: 'italic',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {ticket.lastAction}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Unassigned High-Priority */}
              <Box sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <SectionLabel>Unassigned High-Priority</SectionLabel>
                  <Chip
                    label={`${demoUnassignedTickets.length}`}
                    size="small"
                    sx={{
                      backgroundColor: colorUtils.withOpacity(theme.palette.error.main, 0.15),
                      color: theme.palette.error.main,
                      fontWeight: 700,
                      fontSize: '0.70rem',
                      borderRadius: 1,
                      height: 22,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {demoUnassignedTickets.map((ticket) => (
                    <Box key={ticket.id} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: theme.palette.background.elevated, transform: 'translateX(4px)' },
                    }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {ticket.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                          {ticket.created}
                        </Typography>
                      </Box>
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={{ ...getSeverityChipSx(getPriorityColor(ticket.priority, theme)), flexShrink: 0 }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

            </Box>
          </Grid>

          {/* ═══ RIGHT COLUMN ════════════════════════════════════════════════ */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>

              {/* MTTR Comparison — Socmate hardcoded to 12 min live */}
              {/*<Box sx={cardSx}>*/}
              {/*  <SectionLabel>MTTR Comparison</SectionLabel>*/}
              {/*  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>*/}
              {/*    {[*/}
              {/*      { label: 'Manual (no automation)', value: demoMttr.manualMin, color: theme.palette.error.main,   sublabel: 'Industry benchmark' },*/}
              {/*      { label: 'SOAR',                   value: demoMttr.soarMin,   color: theme.palette.warning.main, sublabel: 'Industry benchmark' },*/}
              {/*      { label: 'Socmate',                value: demoMttr.socmateMin,color: theme.palette.success.main, sublabel: 'Live data' },*/}
              {/*    ].map((row) => (*/}
              {/*      <Box key={row.label}>*/}
              {/*        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>*/}
              {/*          <Box>*/}
              {/*            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>*/}
              {/*              {row.label}*/}
              {/*            </Typography>*/}
              {/*            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>*/}
              {/*              {row.sublabel}*/}
              {/*            </Typography>*/}
              {/*          </Box>*/}
              {/*          <Typography sx={{*/}
              {/*            fontWeight: 700,*/}
              {/*            color: row.color,*/}
              {/*            fontFamily: 'monospace',*/}
              {/*            fontSize: '0.95rem',*/}
              {/*            alignSelf: 'center',*/}
              {/*          }}>*/}
              {/*            {row.value} min*/}
              {/*          </Typography>*/}
              {/*        </Box>*/}
              {/*        <Box sx={{*/}
              {/*          height: 6,*/}
              {/*          borderRadius: 3,*/}
              {/*          backgroundColor: theme.palette.background.tertiary,*/}
              {/*          overflow: 'hidden',*/}
              {/*        }}>*/}
              {/*          <Box sx={{*/}
              {/*            height: '100%',*/}
              {/*            width: `${(row.value / demoMttr.manualMin) * 100}%`,*/}
              {/*            borderRadius: 3,*/}
              {/*            backgroundColor: row.color,*/}
              {/*            transition: 'width 0.5s ease',*/}
              {/*          }} />*/}
              {/*        </Box>*/}
              {/*      </Box>*/}
              {/*    ))}*/}
              {/*  </Box>*/}
              {/*  <Typography variant="caption" sx={{*/}
              {/*    color: theme.palette.text.tertiary,*/}
              {/*    display: 'block',*/}
              {/*    mt: 1.5,*/}
              {/*    fontStyle: 'italic',*/}
              {/*  }}>*/}
              {/*    SANS SOC Survey 2025, IBM Cost of a Data Breach 2025*/}
              {/*  </Typography>*/}
              {/*</Box>*/}

              {/* Top Attack Categories */}
              <Box sx={cardSx}>
                <SectionLabel>Top Attack Categories</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {demoTopCategories.map((cat) => (
                    <Box key={cat.category}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{cat.category}</Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>{cat.count}</Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, backgroundColor: theme.palette.background.tertiary, overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%',
                          width: `${(cat.count / maxCategoryCount) * 100}%`,
                          borderRadius: 3,
                          backgroundColor: theme.palette.primary.main,
                          transition: 'width 0.4s ease',
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Top Affected Clients */}
              <Box sx={cardSx}>
                <SectionLabel>Top Affected Clients</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {demoTopClients.map((client, index) => (
                    <Box key={client.name} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" sx={{
                          color: theme.palette.text.tertiary,
                          fontWeight: 700,
                          width: 18,
                          textAlign: 'center',
                          flexShrink: 0,
                        }}>
                          {index + 1}
                        </Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {client.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${client.alerts} alerts`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.background.tertiary,
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          borderRadius: 1,
                          height: 22,
                          flexShrink: 0,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Alert Volume Trend */}
              <Box sx={{ ...cardSx, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <SectionLabel>Alert Volume Trend</SectionLabel>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', flexGrow: 1, minHeight: 80 }}>
                  {demoTrendData.map((value, index) => (
                    <Box key={index} sx={{
                      flex: 1,
                      height: `${(value / trendMax) * 100}%`,
                      backgroundColor: index === demoTrendData.length - 1
                        ? theme.palette.primary.main
                        : colorUtils.withOpacity(theme.palette.primary.main, 0.25),
                      borderRadius: '2px 2px 0 0',
                      minWidth: 4,
                      cursor: 'pointer',
                      transition: 'height 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
                      '&:hover': { backgroundColor: theme.palette.primary.main, transform: 'scaleY(1.08)', transformOrigin: 'bottom' },
                    }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>24h ago</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>now</Typography>
                </Box>
              </Box>

            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OverviewPageDemo;
